import { useCallback, useEffect, useRef, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import {
  closeSaleCenter,
  getSaleCenterClosingSummary,
  sendBackSaleCenter,
} from "../../services";
import { SaleCenterClosingSummary } from "../../types/sale-center-closing.type";
import { formatNumber, numberWithCommas } from "../../utils";
import { useUserRole } from "../../utils/hooks/useUserRole";
import { featureAccess } from "../../permissions/access";
import { hasNotes } from "../CreditDebitNote/reportFigures";
import ClosingActionBox from "./ClosingActionBox";
import ClosingCreditDebitNoteBox from "./ClosingCreditDebitNoteBox";
import ClosingOutcomeBox from "./ClosingOutcomeBox";
import ClosingReviewerBox from "./ClosingReviewerBox";
import ClosingSentBackAlert from "./ClosingSentBackAlert";
import ClosingStatusChip from "./ClosingStatusChip";
import SendBackClosingDialog from "./SendBackClosingDialog";
import {
  NET_OF_CASH_NOTES_HINT,
  SEND_BACK_LABEL,
  formatBusinessDate,
} from "./saleCenterClosing.utils";

interface SaleCenterClosingPanelProps {
  branchCode: string;
  businessDate: string; // YYYY-MM-DD
  /**
   * Fired after a close or a send-back: closing freezes every deposit in the branch-day and
   * sending back clears its ตรวจแล้ว ticks, so the table above is stale either way.
   */
  onDepositsChanged: () => void | Promise<void>;
  /**
   * Publishes the branch-day's closing state to the table above, which needs it to decide
   * whether a row may be asked to be reopened (คำขอแก้ไขยอดโอน) — the request is only
   * offered on a CLOSED day and only within seven days of `closed_at`.
   *
   * Pushed up rather than fetched again: this is one request for the whole branch-day, and
   * a second copy of it could disagree with the panel the user is reading.
   */
  onSummaryChange?: (summary: SaleCenterClosingSummary | null) => void;
  /**
   * Bumped by the table above after every write it gets accepted on this branch-day's
   * deposits. The summary is counted from those same rows — how many of them each side has
   * ticked, the ยอดที่ต้องโอนรวม in the header, whether the day may be closed at all — so a
   * new value re-reads it instead of leaving the panel on the figures the page opened with.
   */
  depositsRevision?: number;
}

/**
 * ปิดศูนย์เซล for one branch-day — the last check on a day's money.
 *
 * The branch back office and head-office accounting verify each route independently in the
 * table above; this panel is where the head-office accounting SUPERVISOR reconciles the whole
 * branch-day against Express, keys in the cash actually received and freezes the day.
 *
 * Everyone in the review cohort reads the whole panel — the sign-offs, the figures a closed
 * day was signed on, and the closing form itself. Only the supervisor may submit that form;
 * for the branch back office and the plain accountant it is inert and says whose decision it
 * is, so they can see what is about to be decided about their day instead of a panel that
 * stops where the decision starts.
 *
 * The summary is one request for the whole branch-day and is the panel's only source of truth:
 * the close and send-back responses return the same shape, so the panel redraws from what the
 * server just decided rather than reconstructing the new state locally.
 */
export default function SaleCenterClosingPanel({
  branchCode,
  businessDate,
  onDepositsChanged,
  onSummaryChange,
  depositsRevision = 0,
}: SaleCenterClosingPanelProps) {
  const role = useUserRole();
  const canManageClosing = featureAccess.saleCenterClosing(role);
  const [summary, setSummary] = useState<SaleCenterClosingSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [sendBackOpen, setSendBackOpen] = useState<boolean>(false);

  /**
   * Which read of the summary the panel is currently waiting on.
   *
   * Two ticks in quick succession put two reads in flight, and the slower one is not
   * necessarily the older: without this, a response describing the branch-day before the
   * second tick could land last and redraw the panel behind the table. A close or a
   * send-back claims the count too — its answer is newer than any read still out there.
   */
  const latestSummaryRequestRef = useRef<number>(0);

  const applyWrittenSummary = (written: SaleCenterClosingSummary) => {
    latestSummaryRequestRef.current += 1;
    setSummary(written);
  };

  const fetchSummary = useCallback(async () => {
    const requestId = latestSummaryRequestRef.current + 1;
    latestSummaryRequestRef.current = requestId;
    setLoading(true);
    try {
      const {
        data: { data },
      } = await getSaleCenterClosingSummary(branchCode, businessDate);
      if (latestSummaryRequestRef.current !== requestId) return;
      setSummary(data ?? null);
    } catch {
      // The interceptor has already told the user what went wrong. Drop the panel rather
      // than leaving figures from another branch-day on screen — the report itself is
      // unaffected and stays readable.
      if (latestSummaryRequestRef.current === requestId) setSummary(null);
    } finally {
      if (latestSummaryRequestRef.current === requestId) setLoading(false);
    }
  }, [branchCode, businessDate]);

  // On mount, and again on every revision the table publishes: a ตรวจแล้ว written up there
  // changes what this panel reports about the very same rows.
  useEffect(() => {
    fetchSummary();
  }, [fetchSummary, depositsRevision]);

  // Every path that changes the summary — the first load, a close, a send-back, a failed
  // call that reloaded it — goes through this one state, so the table is told from here
  // rather than from each handler.
  useEffect(() => {
    onSummaryChange?.(summary);
  }, [summary, onSummaryChange]);

  const handleConfirmClose = async (
    cashCollectedAmount: number,
    note: string | null,
  ) => {
    setSaving(true);
    try {
      const {
        data: { data },
      } = await closeSaleCenter({
        branch_code: branchCode,
        business_date: businessDate,
        cash_collected_amount: cashCollectedAmount,
        note,
      });
      if (data) applyWrittenSummary(data);
      await onDepositsChanged();
    } catch {
      // Refused (a precondition moved, or someone else closed it first) and already
      // reported by the interceptor. Reload so the panel shows the state that actually
      // holds instead of the one the supervisor was acting on.
      await fetchSummary();
    } finally {
      setSaving(false);
    }
  };

  const openSendBack = () => setSendBackOpen(true);
  const closeSendBack = () => setSendBackOpen(false);

  const handleConfirmSendBack = async (reason: string) => {
    setSaving(true);
    try {
      const {
        data: { data },
      } = await sendBackSaleCenter({
        branch_code: branchCode,
        business_date: businessDate,
        reason,
      });
      if (data) applyWrittenSummary(data);
      setSendBackOpen(false);
      await onDepositsChanged();
    } catch {
      // Same contract as the close above: the failure has been surfaced, and the dialog is
      // left open so the reason the user typed is not lost on a retry.
      await fetchSummary();
    } finally {
      setSaving(false);
    }
  };

  if (loading && !summary) {
    return (
      <Paper sx={{ mt: 2 }}>
        <Skeleton variant="text" width={240} />
        <Skeleton variant="rectangular" height={96} sx={{ mt: 2 }} />
      </Paper>
    );
  }

  if (!summary) return null;

  const isClosed = summary.status === "closed";
  const isSentBack = summary.status === "sent_back";
  // ยอดที่ต้องโอนรวม is net of the day's cash notes, so a day that has notes says so beside it.
  const hasDayNotes =
    summary.credit_debit_note !== undefined && hasNotes(summary.credit_debit_note);

  // ส่งกลับ sits inside the closing form while the day can still be closed, where the whole
  // review cohort sees it — inert for everyone but the supervisor, like the rest of that form.
  // A frozen day has no form left, and a box holding nothing but this one button read as an
  // empty box, so on a closed day the button moves up beside the status and is offered only to
  // the grade the server would accept it from.
  const showHeaderSendBack = isClosed && canManageClosing;

  // The right-hand column is the branch-day's live decision until it is frozen, and the record
  // of that decision afterwards. Never both, and never neither.
  const secondColumn = isClosed ? (
    <ClosingOutcomeBox summary={summary} />
  ) : (
    <ClosingActionBox
      summary={summary}
      canAct={canManageClosing}
      saving={saving}
      onConfirm={handleConfirmClose}
      onSendBack={openSendBack}
    />
  );

  return (
    <Paper sx={{ mt: 2 }}>
      <Stack gap={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            ปิดศูนย์เซล
          </Typography>
          <Stack direction="row" alignItems="center" gap={1}>
            {showHeaderSendBack && (
              <Button
                size="small"
                color="error"
                variant="outlined"
                disabled={saving}
                onClick={openSendBack}
              >
                {SEND_BACK_LABEL}
              </Button>
            )}
            <ClosingStatusChip status={summary.status} spelledOut />
          </Stack>
        </Stack>
        <Typography variant="body2" color="text.secondary">
          สาขา {summary.branch_code} · วันที่{" "}
          {formatBusinessDate(summary.business_date)} ·{" "}
          {numberWithCommas(summary.route_count)} สายส่ง · ยอดที่ต้องโอนรวม{" "}
          {formatNumber(summary.required_transfer_total)} บาท
          {hasDayNotes && ` (${NET_OF_CASH_NOTES_HINT})`}
        </Typography>
        {/* Above the columns and above the form it re-opens: what the branch is being asked to
            fix outranks both, and it is addressed to the branch rather than to head office. */}
        {isSentBack && <ClosingSentBackAlert summary={summary} />}
        {/* The sign-offs and the decision sit side by side. Stacked, the panel ran far enough
            down the page that the two were never read together — and they have to be: the
            right column is where the day is frozen, or what it was frozen on, and the left is
            the evidence behind it. A grid rather than a Stack because the two must end level
            whatever they hold; flex leaves the shorter one stopping halfway down the row.
            Below `md` there is not room for two columns of controls, so they fall back to one
            rather than pushing the page sideways. */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "repeat(2, minmax(0, 1fr))",
            },
            gap: 2,
          }}
        >
          <ClosingReviewerBox summary={summary} />
          {secondColumn}
        </Box>
        <ClosingCreditDebitNoteBox notes={summary.credit_debit_note} />
      </Stack>

      {canManageClosing && (
        <SendBackClosingDialog
          open={sendBackOpen}
          branchCode={branchCode}
          businessDate={businessDate}
          saving={saving}
          onClose={closeSendBack}
          onConfirm={handleConfirmSendBack}
        />
      )}
    </Paper>
  );
}
